import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id] - Variant ID
 * @property {string} [title] - Variant title
 * @property {string} [name] - Variant name
 * @property {boolean} [available] - Whether variant is available
 * @property {Object} [featured_media] - Featured media object
 * @property {Object} [featured_media.preview_image] - Preview image data
 * @property {string} [featured_media.preview_image.src] - Image source URL
 * @property {string} [featured_media.alt] - Alt text for the image
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 * @property {HTMLElement} quantityDisplay - Quantity display container
 * @property {HTMLElement} quantityNumber - Quantity number element
 * @property {HTMLImageElement} productImage - Product image element
 * @property {HTMLElement} [currentPrice] - Current variant price
 * @property {HTMLElement} [comparePrice] - Current variant compare-at price
 */

/**
 * A custom element that manages a sticky add-to-cart bar.
 * Shows when the main buy buttons scroll out of view.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton', 'quantityDisplay', 'quantityNumber'];

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {IntersectionObserver | null} */
  #mainBottomObserver = null;

  /** @type {HTMLElement | null} */
  #buyButtonsBlock = null;

  /** @type {HTMLElement | null} */
  #footer = null;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {number | null} */
  #animationTimeout = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {boolean} */
  #hiddenByBottom = false;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    window.addEventListener('scroll', this.#syncStickyVisibility, {
      passive: true,
      signal,
    });

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.#setupIntersectionObserver(), {
        once: true,
        signal,
      });
    } else {
      this.#setupIntersectionObserver();
    }

    const target = this.closest('.shopify-section');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected, { signal });

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.cartError, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });

    this.#getInitialQuantity();

    // Izzat v7: single source of selection = main form quantity-breaks of THIS section/product.
    const syncSoon = () => requestAnimationFrame(() => this.#syncFromMain());
    target?.addEventListener('change', syncSoon, { signal });
    target?.addEventListener('click', syncSoon, { signal });
    this.querySelector('.sticky-add-to-cart__info')?.addEventListener('click', () => this.#scrollToMainSelector(), { signal });
    this.addEventListener('keydown', (event) => {
      if ((event.key === 'Enter' || event.key === ' ') && event.target?.matches?.('.sticky-add-to-cart__variant')) {
        event.preventDefault();
        this.#scrollToMainSelector();
      }
    }, { signal });
    this.#syncFromMain();
  }

  /** @type {MutationObserver | null} */
  #mainPriceObserver = null;

  /** Izzat v7: read units / design names / CTA price from the main form and mirror them in the bar. */
  #syncFromMain() {
    const section = this.closest('.shopify-section');
    const productForm = this.#getProductForm();
    if (!section || !productForm) return;

    const picker = this.querySelector('.sticky-add-to-cart__variant-picker');
    if (picker instanceof HTMLElement) {
      picker.hidden = true;
      picker.style.display = 'none';
    }
    const select = this.querySelector('.sticky-add-to-cart__variant-select');
    if (select instanceof HTMLSelectElement) select.disabled = true;

    const checked = section.querySelector('input[type="radio"][name="quantity"]:checked');
    const units = Number(checked instanceof HTMLInputElement ? checked.value : 1) || 1;
    const block = checked?.id
      ? section.querySelector(`label[for="${CSS.escape(checked.id)}"]`)
      : null;
    const names = block
      ? Array.from(block.querySelectorAll('select.quantity-break__variant-select')).map((el) =>
          (/** @type {HTMLSelectElement} */ (el).selectedOptions[0]?.textContent || '').trim()
        ).filter(Boolean)
      : [];
    const title = this.querySelector('.sticky-add-to-cart__variant');
    if (title) {
      const label = names.length ? names.join(' + ') : (title.dataset.izzatBase ??= (title.textContent || '').trim());
      title.textContent = `${units} ${units === 1 ? 'unit' : 'units'} · ${label}`;
      title.setAttribute('role', 'button');
      title.setAttribute('tabindex', '0');
    }

    const mainPrice = productForm.querySelector('[data-atc-price], .atc-bundle-price');
    const priceText = (mainPrice?.textContent || '').trim();
    if (this.refs.currentPrice && priceText) this.refs.currentPrice.textContent = priceText;
    if (this.refs.comparePrice) this.refs.comparePrice.hidden = true;

    if (mainPrice && !this.#mainPriceObserver) {
      this.#mainPriceObserver = new MutationObserver(() => this.#syncFromMain());
      this.#mainPriceObserver.observe(mainPrice, { childList: true, characterData: true, subtree: true });
      this.#abortController.signal.addEventListener('abort', () => this.#mainPriceObserver?.disconnect());
    }

    this.#currentQuantity = units;
    this.#updateButtonText();
  }

  /** Izzat v7: the selector lives only in the main form; tapping the summary scrolls to it. */
  #scrollToMainSelector() {
    const section = this.closest('.shopify-section');
    (section?.querySelector('.quantity-break') || this.#getProductForm())?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#mainBottomObserver?.disconnect();
    this.#abortController.abort();
    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
    }
  }

  /**
   * Sets up the IntersectionObserver to watch the buy buttons visibility
   */
  #setupIntersectionObserver() {
    const productForm = this.#getProductForm();
    if (!productForm) return;

    const buyButtonsBlock = productForm.closest('.buy-buttons-block');
    if (!buyButtonsBlock) return;

    // In themes migrated from 2.0, the footer element doesn't exist
    // Izzat fix: usar o ULTIMO <footer> do documento - apps de review injetam
    // <footer> dentro de cards no meio da pagina, e o primeiro match faria a
    // barra sumir cedo demais (mesma regra do script inline da secao).
    const allFooters = document.querySelectorAll('footer');
    const footer = allFooters[allFooters.length - 1] ?? document.querySelector('[class*="footer-group"]');
    if (!footer) return;

    this.#buyButtonsBlock = buyButtonsBlock;
    this.#footer = /** @type {HTMLElement} */ (footer);

    // Observer for buy buttons visibility
    this.#buyButtonsIntersectionObserver = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (!entry) return;

      // Only show sticky bar if buy buttons have been scrolled past (above viewport)
      if (!entry.isIntersecting && !this.#isStuck) {
        // Check if the element is above the viewport (scrolled past) or below (not yet reached)
        const rect = entry.target.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top < 0) {
          // Element is above viewport - show sticky bar
          this.#showStickyBar();
        }
        // If rect.top >= 0, element is below viewport - don't show sticky bar yet
      } else if (entry.isIntersecting && this.#isStuck) {
        this.#hiddenByBottom = false;
        this.#hideStickyBar();
      }
    });

    // Observer for footer visibility - hides sticky bar at page bottom
    this.#mainBottomObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry) return;

        if (entry.isIntersecting && this.#isStuck) {
          this.#hiddenByBottom = true;
          this.#hideStickyBar();
        } else if (!entry.isIntersecting && this.#hiddenByBottom) {
          // Footer out of view - check if we should show sticky bar again
          const rect = buyButtonsBlock.getBoundingClientRect();
          // Only show if buy buttons are above the viewport (scrolled past)
          if (rect.bottom < 0 || rect.top < 0) {
            this.#hiddenByBottom = false;
            this.#showStickyBar();
          }
        }
      },
      {
        rootMargin: '200px 0px 0px 0px',
      }
    );

    this.#buyButtonsIntersectionObserver.observe(buyButtonsBlock);
    this.#mainBottomObserver.observe(footer);
    this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
    this.#syncStickyVisibility();
  }

  // Public action handlers
  /**
   * Handles the add to cart button click in the sticky bar
   */
  /**
   * Updates the independent sticky-bar variant selection.
   * @param {Event} event
   */
  handleStickyVariantChange = (event) => {
    const selector =
      event.target instanceof HTMLSelectElement
        ? event.target
        : this.querySelector('.sticky-add-to-cart__variant-select');
    if (!selector) return;

    const selectedOption = selector.selectedOptions[0];
    if (!selectedOption) return;

    this.dataset.currentVariantId = selectedOption.value;
    this.dataset.variantAvailable = selectedOption.dataset.available;
    this.refs.addToCartButton.disabled = selectedOption.dataset.available !== 'true';

    const variantTitle = this.querySelector('.sticky-add-to-cart__variant');
    if (variantTitle) {
      variantTitle.textContent = selectedOption.textContent?.replace(/\s+-\s+Sold out\s*$/, '').trim() || '';
    }

    if (this.refs.currentPrice && selectedOption.dataset.price) {
      this.refs.currentPrice.textContent = selectedOption.dataset.price;
    }

    if (this.refs.comparePrice) {
      const comparePrice = selectedOption.dataset.comparePrice;
      this.refs.comparePrice.textContent = comparePrice || '';
      this.refs.comparePrice.hidden = !comparePrice;
    }

    if (this.refs.productImage && selectedOption.dataset.image) {
      this.refs.productImage.src = selectedOption.dataset.image;
      this.refs.productImage.removeAttribute('srcset');
    }
  };

  /**
   * Handles variant update events
   * @param {CustomEvent} event - The variant update event
   */
  #handleVariantUpdate = (event) => {
    if (event.detail.data.productId !== this.dataset.productId) return;

    const variant = event.detail.resource;

    // Get the new sticky add to cart HTML from the server response
    const newStickyAddToCart = event.detail.data.html.querySelector('sticky-add-to-cart');
    if (!newStickyAddToCart) return;

    const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
    if (!newStickyBar) return;

    // Store current visibility state before morphing
    const currentStuck = this.refs.stickyBar.getAttribute('data-stuck') || 'false';
    const variantAvailable = newStickyAddToCart.dataset.variantAvailable;

    // Morph the entire sticky bar content
    morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

    // Restore visibility state after morphing
    this.refs.stickyBar.setAttribute('data-stuck', currentStuck);
    this.dataset.variantAvailable = variantAvailable;

    // Update the dataset attributes with new variant info
    if (variant && variant.id) {
      this.dataset.currentVariantId = variant.id;
    }

    // Re-cache the target add to cart button after morphing
    const productForm = this.#getProductForm();
    if (productForm) {
      this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
    }

    if (variant == null) {
      this.#handleVariantUnavailable();
    }
    // Restore the current quantity display if needed
    this.#updateButtonText();
    this.#syncFromMain();
  };

  /**
   * Handles variant selected events
   * @param {CustomEvent} event - The variant selected event
   */
  #handleVariantSelected = (event) => {
    // The variant update event will follow and handle all updates via morph
    // We just update the dataset here for tracking
    const variantId = event.detail.resource?.id;
    if (!variantId) return;
    this.dataset.currentVariantId = variantId;
  };

  /**
   * Updates the variant title based on selected options when the variant is unavailable
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';
    const variantTitleElement = this.querySelector('.sticky-add-to-cart__variant');
    const productId = this.dataset.productId;
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(variantPicker.querySelectorAll('input:checked'))
      .map((option) => /** @type {HTMLInputElement} */ (option).value)
      .filter((value) => value !== '')
      .join(' / ');
    if (!selectedOptions) return;
    variantTitleElement.textContent = selectedOptions;
  };

  /**
   * Handles cart add complete (success or error) - resets puppet flag
   * @param {CustomEvent} _event - The cart event (unused)
   */
  #handleCartAddComplete = (_event) => {
    // Reset the puppet flag after cart operation
    if (this.#targetAddToCartButton) {
      this.#targetAddToCartButton.dataset.puppet = 'false';
    }
  };

  /**
   * Handles quantity selector update events
   * @param {QuantitySelectorUpdateEvent} event - The quantity update event
   */
  #handleQuantityUpdate = (event) => {
    // The sticky bar is intentionally a single-unit quick add.
    if (event.detail.cartLine) return;
    this.#syncFromMain();
  };

  #syncStickyVisibility = () => {
    if (!this.#buyButtonsBlock) {
      this.#buyButtonsBlock = this.#getProductForm()?.closest('.buy-buttons-block') ?? null;
    }

    if (!this.#footer) {
      // Izzat fix: mesmo criterio de ULTIMO <footer> usado no setup.
      const allFooters = document.querySelectorAll('footer');
      this.#footer = /** @type {HTMLElement | null} */ (
        allFooters[allFooters.length - 1] ?? document.querySelector('[class*="footer-group"]')
      );
    }

    if (!this.#buyButtonsBlock || !this.#footer) return;

    const buyButtonsRect = this.#buyButtonsBlock.getBoundingClientRect();
    const footerRect = this.#footer.getBoundingClientRect();
    const hasPassedBuyButtons = buyButtonsRect.bottom < 0;
    const isNearFooter = footerRect.top <= window.innerHeight + 200;

    if (hasPassedBuyButtons && !isNearFooter && !this.#isStuck) {
      this.#showStickyBar();
    } else if ((!hasPassedBuyButtons || isNearFooter) && this.#isStuck) {
      this.#hideStickyBar();
    }
  };

  /**
   * Shows the sticky bar with animation
   */
  #showStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = true;
    stickyBar.dataset.stuck = 'true';
  }

  /**
   * Hides the sticky bar with animation
   */
  #hideStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = false;
    stickyBar.dataset.stuck = 'false';
  }

  // Helper methods
  /**
   * Gets the product form element
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');
    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component:not(.sticky-add-to-cart__form)[data-product-id="${productId}"]`
    );
  }

  /**
   * Gets the initial quantity from the data attribute
   */
  #getInitialQuantity() {
    this.#currentQuantity = 1;
    this.#updateButtonText();
  }

  /**
   * Updates the button text to include quantity
   */
  #updateButtonText() {
    const { addToCartButton, quantityDisplay, quantityNumber } = this.refs;

    const available = !addToCartButton.disabled;

    // Update the quantity number
    quantityNumber.textContent = this.#currentQuantity.toString();

    // Show/hide the quantity display based on availability and quantity
    if (available && this.#currentQuantity > 1) {
      quantityDisplay.style.display = 'inline';
    } else {
      quantityDisplay.style.display = 'none';
    }
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
